import { useMutation, UseMutationOptions } from '@tanstack/react-query';
import { AxiosError, AxiosProgressEvent } from 'axios';
import { RecordTag } from '@/constants/documentTags.ts';
import { Documents } from '@/client/sdk.gen.ts';
import type {
	UploadDocumentsV2V2HousesHouseIdDocumentsPostError,
	UploadDocumentsV2V2HousesHouseIdDocumentsPostResponse,
} from '@/client/types.gen.ts';
import { normalizeFileMimeType } from '@/utils/upload/uploadUtils.ts';

interface UploadFileParams {
	houseId: number;
	file?: File;
	text?: string;
	tags?: RecordTag[] | null;
	private?: boolean;
	signal?: AbortSignal;
	/**
	 * Fraction of the request body written out, 0 to 1. Reports the transfer only:
	 * reaching 1 means the browser has sent every byte, not that the server has
	 * accepted them — that is what the 202 this resolves on means.
	 */
	onTransferProgress?: (fraction: number) => void;
	/**
	 * The created record's public id, which only the upload response carries — the mutation
	 * resolves to the numeric id, and waiting for the documents list to refetch would be a race.
	 * Absent when the content was already present, since nothing was created.
	 */
	onRecordCreated?: (recordPublicId: string) => void;
}

/**
 * Hook for uploading a document to house documents using the v2 endpoint.
 * Supports both file uploads and text-only documents with description.
 */
export const useUploadDocument = (
	options?: Partial<
		UseMutationOptions<
			number | null,
			AxiosError<UploadDocumentsV2V2HousesHouseIdDocumentsPostError>,
			UploadFileParams
		>
	>,
) => {
	return useMutation<number | null, AxiosError<UploadDocumentsV2V2HousesHouseIdDocumentsPostError>, UploadFileParams>(
		{
			...options,
			mutationFn: async ({
				houseId,
				file,
				text,
				tags,
				private: isPrivate,
				signal,
				onTransferProgress,
				onRecordCreated,
			}) => {
				const { data } = (await Documents.uploadDocumentsV2V2HousesHouseIdDocumentsPost({
					path: { house_id: houseId },
					body: {
						file: file ? normalizeFileMimeType(file) : undefined,
						description: text ?? undefined,
						tags: tags ?? undefined,
						private: isPrivate ?? false,
					},
					signal,
					// Axios reports this from the browser's send phase, so it exists only
					// while bytes are moving and stops before the response arrives.
					onUploadProgress: onTransferProgress
						? (event: AxiosProgressEvent) => {
								const fraction =
									event.progress ?? (event.total ? event.loaded / event.total : undefined);
								if (fraction !== undefined) onTransferProgress(fraction);
							}
						: undefined,
					throwOnError: true,
				})) as { data: UploadDocumentsV2V2HousesHouseIdDocumentsPostResponse };
				// The upload is a 202 either way; the outcome says whether a document was
				// created or the content was already in this Bundle (null = skipped).
				if (data.outcome === 'skipped_already_present') return null;
				if (!data.document) throw new Error('Upload reported created but returned no document');
				if (data.document.public_id) onRecordCreated?.(data.document.public_id);
				return data.document.id;
			},
		},
	);
};
